import {ActionMenu} from '@primer/react-brand/esm'
import {GlobeIcon} from '@primer/octicons-react'
import {setCookie} from '@github-ui/cookies'
import {sendEvent} from '@github-ui/hydro-analytics'

import type {LocaleConfig} from '../../marketing-footer.types'

import styles from './LocaleSelector.module.css'

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000

type Props = {
  locale: LocaleConfig
  /** Visible/hidden label for the menu overlay (`aria-label`). */
  overlayLabel: string
}

function handleSelect(value: string) {
  sendEvent('select_language.click', {
    category: 'Locale Picker',
    action: 'click_to_select_language',
    label: `locale:${value}`,
  })

  const expiration = new Date(Date.now() + ONE_WEEK_MS).toUTCString()
  setCookie('locale', value, expiration)

  if (typeof window !== 'undefined') {
    const currentUrl = new URL(window.location.href, window.location.origin)
    currentUrl.searchParams.set('locale', value)
    window.location.href = currentUrl.toString()
  }
}

export function LocaleSelector({locale, overlayLabel}: Props) {
  const {triggerAriaLabel, options, currentLocale} = locale

  const current = options.find(o => o.locale.startsWith(currentLocale.slice(0, 2))) ?? options[0]

  if (!current) {
    return null
  }

  return (
    <ActionMenu menuAlignment="start" onSelect={handleSelect} selectionVariant="single" size="small">
      <ActionMenu.Button aria-label={triggerAriaLabel} variant="secondary">
        <span className={styles.triggerContent}>
          <GlobeIcon size={16} />
          <span className={styles.triggerLabel}>{current.label}</span>
        </span>
      </ActionMenu.Button>

      <ActionMenu.Overlay aria-label={overlayLabel}>
        {options.map(option => {
          const isSelected = option.locale === current.locale
          return (
            <ActionMenu.Item key={option.locale} value={option.locale} selected={isSelected}>
              {option.label}
            </ActionMenu.Item>
          )
        })}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
